import { type RulebookDeletionResult, useRulebookDeletion, useTranslation } from "@next-platform/client-common-core";
import React, { useCallback, useMemo, useRef, useState } from "react";

import Trans from "@/components/Trans/Trans";

type UseRulebookDeleteConfirmationOptions = {
  draftId: string;
  name: string;
  /** Runs once the delete landed; not after a failed server delete. */
  onDeleted?: () => void;
};

type Translate = ReturnType<typeof useTranslation>["t"];

const deleteErrorMessage = (result: Exclude<RulebookDeletionResult, "deleted">, translate: Translate): string => {
  switch (result) {
    case "published":
      return translate("rulebookEditor:card.deleteNowPublished");
    case "serverFailed":
      return translate("rulebookEditor:card.deleteRulebookFailed");
    case "draftFailed":
      return translate("rulebookEditor:card.deleteDraftFailed");
  }
};

/**
 * The confirmation behind the Editor's delete actions. An unpublished draft is deleted locally. A published one is
 * deleted as a Rulebook (server first, which detaches it everywhere), since dropping only the draft would strand it.
 * A failed server delete keeps the modal open with an error, and keeps the draft.
 */
export default function useRulebookDeleteConfirmation({ draftId, name, onDeleted }: UseRulebookDeleteConfirmationOptions) {
  const { t } = useTranslation();
  const { isPublished, deleteDraft, deleteRulebook } = useRulebookDeletion({ draftId });
  const [stateIsOpen, setStateIsOpen] = useState(false);
  const [stateIsDeleting, setStateIsDeleting] = useState(false);
  // The server delete can't be cancelled: while it runs the dialog can't be dismissed or confirmed again.
  const refIsDeleting = useRef(false);

  const open = useCallback(() => setStateIsOpen(true), []);
  const close = useCallback(() => {
    if (!refIsDeleting.current) {
      setStateIsOpen(false);
    }
  }, []);
  const confirm = useCallback(async () => {
    if (refIsDeleting.current) {
      return undefined;
    }
    refIsDeleting.current = true;
    setStateIsDeleting(true);
    try {
      // A draft published elsewhere since the dialog opened is left alone; the dialog now shows the Rulebook warning.
      const result = isPublished ? await deleteRulebook() : deleteDraft();
      if (result !== "deleted") {
        return { error: deleteErrorMessage(result, t) };
      }
    } finally {
      refIsDeleting.current = false;
      setStateIsDeleting(false);
    }
    onDeleted?.();
    return undefined;
  }, [isPublished, deleteDraft, deleteRulebook, onDeleted, t]);

  const values = useMemo(() => ({ name }), [name]);
  const message = isPublished ? (
    <Trans i18nKey="rulebookEditor:card.deleteRulebookMessage" values={values} />
  ) : (
    <Trans i18nKey="rulebookEditor:card.deleteMessage" values={values} />
  );
  const heading = isPublished ? t("rulebookEditor:card.deleteRulebookHeading") : t("rulebookEditor:card.deleteHeading");

  return { isPublished, isOpen: stateIsOpen, isDeleting: stateIsDeleting, open, close, confirm, heading, message };
}
